import Vue from 'vue';
import i18n from '@/i18n';
import { formatOrderItem, formatOrderOptionsToCompare } from '@/utils/format';
import { compareItem } from '@/utils/membership-helpers';
import { sortOrderOptions } from '@/utils/sort';
import { isReplenishOrExtensionSection } from '@/utils/general';
import { DateTime } from 'luxon';
import { MutationTree } from 'vuex';
import { state as authState } from '../auth/auth';
import { OrdersState } from '../types';

/**
 * Calculate order item price, including options and sub-options
 *
 * @param {MenuOrderItem} orderItem
 * @return {number}
 */
function calculateOrderItemPrice(orderItem: MenuOrderItem): number {
	let itemPrice = +(orderItem.price || 0);
	const optionGroups: OrderOptionGroup[] | null = orderItem.orderOptions;
	let optionsPrice: number = 0;

	if (optionGroups && optionGroups.length) {
		// Set the base price of the item if a pricing option is present
		const pricingOption = optionGroups.find((option: OrderOptionGroup) => option.optionGroupType === 'pricing');
		if (pricingOption) {
			itemPrice = +(pricingOption.values[0].price || 0);
		}

		// Calculate the price of the options
		optionsPrice = optionGroups.reduce((acc: number, optionGroup: OrderOptionGroup) => {
			let optionPrice = 0;

			// Ignore pricing options when calculation option price
			if (optionGroup.optionGroupType !== 'pricing') {
				optionGroup.values.forEach((option: OrderOption) => {
					if (option.price) {
						optionPrice += +(option.price) * option.quantity;
					}

					// If sub-options are present, calculate their price and add it to the option price
					if (option.options) {
						option.options.forEach((subOptionGroup: OrderOptionGroup) => {
							subOptionGroup.values.forEach((subOption: OrderOption) => {
								if (subOption.price) {
									optionPrice += +(subOption.price) * subOption.quantity;
								}
							});
						});
					}
				});
			}
			return acc + optionPrice;
		}, 0);
	}

	return (itemPrice + optionsPrice) * orderItem.quantity;
}

/**
 * Check if the item has pricing options, then check if the item has a pricing option selected
 * If item.orderOptions doesn't have a pricing option group selected, we add the price_type (default price name) as the default
 * This is for visual purposes only, this option will be ignored by the Remy when validating the item and setting costs
 *
 * @param {OrderItem} item
 * @param {OptionGroup[]} selectedItemOptions
 * @return {void}
 */
function addDefaultPricingOption(item: OrderItem, selectedItemOptions: OptionGroup[]): void {
	const hasPricingOptions = !!(selectedItemOptions?.find((optionGroup: OptionGroup) => optionGroup.type === 'pricing' && optionGroup.values?.length));
	if (hasPricingOptions) {
		const pricingOptionSelected = !!(item.orderOptions?.find(option => option.optionGroupType === 'pricing'));

		if (!pricingOptionSelected) {
			item.orderOptions = [
				{
					name: 'pricing',
					optionGroupType: 'pricing',
					defaultPricingOption: true,
					required: false,
					allow_multiple_selection: false,
					values: [{
						name: item.price_type,
						price: item.price,
						quantity: 1,
						roll_up: true,
						localization: Object.keys(item.localization || {}).reduce((acc, locale) => {
							const priceType = item.localization?.[locale]?.price_type;
							if (priceType) {
								acc[locale] = { name: priceType };
							}
							return acc;
						}, {} as Record<string, { name: string }>)
					}]
				},
				...(item.orderOptions || [])
			];
		}
	}
}

/**
 * Check whether a cart item's discount differs from the given discount field by field.
 *
 * @param {CostsDiscount | null} [discount]
 * @param {CostsDiscount} [currentDiscount]
 * @return {boolean}
 */
function hasDiscountChanged(discount?: CostsDiscount | null, currentDiscount?: CostsDiscount): boolean {
	return discount?.code !== currentDiscount?.code
		|| discount?.amount !== currentDiscount?.amount
		|| discount?.type !== currentDiscount?.type
		|| discount?.percentageValue !== currentDiscount?.percentageValue
		|| discount?.manual !== currentDiscount?.manual;
}

/**
 * Set or clear the discount on the cart item matching the given cart index, if the discount has changed.
 *
 * @param {OrdersState} state
 * @param {number} [cartIndex]
 * @param {CostsDiscount | null} [discount]
 * @return {void}
 */
function setCartItemDiscount(state: OrdersState, cartIndex?: number, discount?: CostsDiscount | null): void {
	const index = state.cart.items.findIndex((cartItem: MenuOrderItem) => cartItem.cartIndex === cartIndex);
	if (index === -1) {
		return;
	}
	if (!hasDiscountChanged(discount, state.cart.items[index].discount)) {
		return;
	}
	const updatedCartItem: MenuOrderItem = { ...state.cart.items[index] };
	if (discount) {
		updatedCartItem.discount = discount;
	}
	else {
		delete updatedCartItem.discount;
	}
	Vue.set(state.cart.items, index, updatedCartItem);
}

export const mutations: MutationTree<OrdersState> = {
	SET_CURRENT_ORDERS(state: OrdersState, currentOrders: Order[]) {
		Vue.set(state, 'currentOrders', currentOrders);
	},

	SET_ORDER_INFO(state: OrdersState, order: Order) {
		const { checkout, config, costs, items, chosenCreditCard } = order.data;
		let deliveryInfo = checkout.delivery;

		// Reset delivery info if the delivery type has changed
		// Catering events always use address delivery and ignore the restaurant's delivery settings,
		// so keep their saved address/coordinates regardless of the configured type
		if (!checkout.cateringEvent && deliveryInfo?.type !== authState.restaurant.delivery?.type) {
			deliveryInfo = {
				type: authState.restaurant.delivery?.type,
				address: ''
			};
		}

		Vue.set(state, 'selectedOrder', order);
		const takeoutType = checkout.cateringEvent ? 'catering-event' : checkout.pickup?.delivery ? 'delivery' : 'pickup';

		Vue.set(state, 'orderDetails', {
			type: order.type,
			tableNum: config.tableInfo.tableNum,
			tableLocation: config.tableInfo.tableLocation,
			tableArea: config.tableInfo.tableArea,
			checkNum: config.checkNum,
			takeoutType
		});
		Vue.set(state, 'cart', {
			items: items.map((item: OrderItem, index: number) => {
				return {
					...item,
					cartIndex: index
				};
			}),
			cartIndex: items.length
		});
		Vue.set(state, 'checkout', {
			pickup: {
				...checkout.pickup,
				dueByDate: order.pickup_time ? DateTime.fromISO(order.pickup_time).toISODate() : null,
				dueByTime: order.pickup_time ? DateTime.fromISO(order.pickup_time).toFormat(i18n.t('orders.guest_details.takeout.timepicker_format')) : null
			},
			delivery: deliveryInfo,
			contact: {
				email: checkout.contact.email,
				phone_number: checkout.contact.phone_number,
				full_name: checkout.contact.full_name
			},
			memberInfo: checkout.memberInfo,
			cateringEvent: checkout.cateringEvent || null,
			quoteInfo: checkout.quoteInfo || null,
			questions: checkout.questions,
			invoice: checkout.invoice || false,
			costCenter: checkout.costCenter || '',
			purchaseOrder: checkout.purchaseOrder || ''
		});
		Vue.set(state, 'costs', costs);
		Vue.set(state, 'chosenCreditCard', chosenCreditCard);
	},

	/**
	 * Turn the order SET_ORDER_INFO just loaded into a new, unsaved one: no source order to update,
	 * no delivery date or time carried over, and no member info reapplied. The catering event is only
	 * kept, without its dates, when the chosen takeout type is still a catering event, and the quote
	 * info only when quoting.
	 *
	 * @param {OrdersState} state
	 * @param {RepeatOrderNotices} notices - what the repeat dropped or repriced, for the cart banners
	 * @return {void}
	 */
	SET_REPEAT_ORDER_INFO(state: OrdersState, notices: RepeatOrderNotices) {
		Vue.set(state, 'repeatOrderNotices', notices);
		Vue.set(state, 'selectedOrder', {});
		Vue.set(state, 'chosenCreditCard', null);
		Vue.set(state.checkout, 'pickup', { ...state.checkout.pickup, dueByDate: null, dueByTime: null });
		Vue.set(state.checkout, 'memberInfo', null);
		Vue.set(state.checkout, 'cateringEvent', state.orderDetails.takeoutType === 'catering-event' && state.checkout.cateringEvent
			? { ...state.checkout.cateringEvent, eventStartDate: '', eventEndDate: '' }
			: null);
		if (!state.isQuoting) {
			Vue.set(state.checkout, 'quoteInfo', null);
		}
	},

	SET_ORDER_VALIDATION_ERRORS(state: OrdersState, errors: { [key: string]: string[] }) {
		Vue.set(state, 'orderValidationErrors', errors);
	},

	SET_MODIFYING_ORDER(state: OrdersState, modifyingOrder: boolean) {
		Vue.set(state, 'modifyingOrder', modifyingOrder);
	},

	SET_IS_QUOTING(state: OrdersState, isQuoting: boolean) {
		Vue.set(state, 'isQuoting', isQuoting);
	},

	SET_EDITING_ITEM(state: OrdersState, editingItem: boolean) {
		Vue.set(state, 'editingItem', editingItem);
	},

	SET_ORDER_DETAILS(state: OrdersState, orderDetails: OrderDetails) {
		const { type, takeoutType } = orderDetails;
		Vue.set(state, 'orderDetails', orderDetails);

		if (type === 'take-out' || type === 'generic-catering') {
			Vue.set(state.checkout, 'pickup', { ...state.checkout.pickup, delivery: takeoutType === 'delivery' });
		}
	},

	SET_SELECTED_ITEM(state: OrdersState, selectedItem: MenuItem) {
		Vue.set(state, 'selectedItem', selectedItem);
	},

	SET_SELECTED_SECTION_SLUG(state: OrdersState, sectionSlug: string) {
		Vue.set(state, 'selectedSectionSlug', sectionSlug);
	},

	SET_ORDER_ITEM(state: OrdersState, payload: { selectedItem: MenuItem | MenuOrderItem; formatItem: boolean }) {
		const { selectedItem, formatItem } = payload;
		let orderItem = selectedItem;
		if (formatItem) {
			orderItem = formatOrderItem({ ...selectedItem as MenuItem, options: [] });
		}
		Vue.set(state, 'orderItem', orderItem);
	},

	SET_ORDER_ITEM_OPTIONS(state: OrdersState, orderItemOptions: OrderOptionGroup[]) {
		if (!state.orderItem) {
			return;
		}

		// If a pricing option is removed from the selected options, revert the item back to it's base price
		const itemHasPricingOptions = !!(state.selectedItem?.options?.some((optionGroup: OptionGroup) => optionGroup.type === 'pricing' && optionGroup.values?.length));
		const pricingOptionSelected = orderItemOptions.some((optionGroup: OrderOptionGroup) => optionGroup.optionGroupType === 'pricing');
		if (itemHasPricingOptions && !pricingOptionSelected) {
			Vue.set(state.orderItem, 'price', state.selectedItem!.price);
		}

		Vue.set(state.orderItem, 'orderOptions', orderItemOptions);
		Vue.set(state.orderItem, 'orderPrice', calculateOrderItemPrice(state.orderItem));
	},

	SET_ORDER_ITEM_QUANTITY(state: OrdersState, quantity: number) {
		if (!state.orderItem) {
			return;
		}
		Vue.set(state.orderItem, 'quantity', quantity);
		Vue.set(state.orderItem, 'orderPrice', calculateOrderItemPrice(state.orderItem));
	},

	ADD_TO_CART(state: OrdersState, specialInstructions: string) {
		if (!state.orderItem) {
			return;
		}

		const orderItem = state.orderItem!;
		const items = state.cart.items;

		if (specialInstructions) {
			const memoOption = orderItem.orderOptions?.find((option: OrderOptionGroup) => option.memo);
			if (memoOption) {
				memoOption.values[0].name = specialInstructions;
			}
			else {
				state.orderItem.orderOptions!.push({
					name: i18n.t('orders.item_viewer.special_instructions'),
					memo: true,
					values: [
						{
							name: specialInstructions,
							price: '0',
							quantity: 1,
							roll_up: true
						}
					]
				});
			}
		}
		addDefaultPricingOption(orderItem, state.selectedItem?.options || []);
		orderItem.orderOptions = sortOrderOptions(orderItem.orderOptions || []);

		// Store the section slug with the cart item for later order splitting
		orderItem.sectionSlug = state.selectedSectionSlug;

		// Add flag to identify replenish/extend items for order splitting
		orderItem.isReplenishExtend = isReplenishOrExtensionSection(state.selectedSectionSlug);

		if(items.length) {
			let existInArray = false;
			let orderOptionsSame = false;
			let prevCartIndex = -1;
			/**
			 * We loop through the items to check if the item already exists in the array,
			 * from there we check if the order options are the same, if they are we simply increase the quantity.
			 */
			for (let index = 0; index < items.length; index++) {
				if (items[index].id === orderItem.id && items[index].openItem === orderItem.openItem) {
					existInArray = true;
					const tempItemOrderOptions = formatOrderOptionsToCompare(orderItem.orderOptions);
					const itemToCompareOrderOptions = formatOrderOptionsToCompare(items[index].orderOptions as OrderOptionGroup[]);
					if (JSON.stringify(tempItemOrderOptions) === JSON.stringify(itemToCompareOrderOptions) && (orderItem.discount?.percentageValue ?? null) === (items[index].discount?.percentageValue ?? null)) {

						// If we are editing an item, we don't want to compare the item being edited with itself
						if (!state.editingItem || (state.editingItem && items[index].cartIndex !== orderItem.cartIndex)) {
							orderOptionsSame = true;
							prevCartIndex = orderItem.cartIndex!;
							// Update order item quantity, price and id, if the item already exists in the cart
							orderItem.quantity += items[index].quantity;
							orderItem.orderPrice = items[index].orderPrice! + orderItem.orderPrice!;
							orderItem.cartIndex = items[index].cartIndex;
							items.splice(index, 1, orderItem);
							break;
						}
					}
				}
			}

			if (state.editingItem) {
				// If the item doesn't have the same options as another item in the cart, we find and replace the item being edited in the cart with the updated item
				if (!orderOptionsSame) {
					const index = items.findIndex((item: MenuOrderItem) => item.cartIndex === orderItem.cartIndex);
					if (index !== -1) {
						items.splice(index, 1, orderItem);
					}
				}
				// If the item has the same options as another item, we remove item being edited from the cart because we just increase the quantity of the existing item
				else {
					const index = items.findIndex((item: MenuOrderItem) => item.cartIndex === prevCartIndex);
					if (index !== -1) {
						items.splice(index, 1);
					}
				}
			}

			/**
			 * If the item exists in the array, we check if the options were the same
			 * so we don't add the item twice
			 */
			if(existInArray) {
				// Don't add a new item if we are editing an existing item
				if(!orderOptionsSame && !state.editingItem) {
					orderItem.cartIndex = state.cart.cartIndex++;
					state.cart.items.push(orderItem);
				}
			}

			/**
			 * If the item does not exists in the array, we simply add it
			 */
			else {
				orderItem.cartIndex = state.cart.cartIndex++;
				state.cart.items.push(orderItem);
			}
		}

		/**
		 * Array empty so we simply add to it
		 */
		else {
			orderItem.cartIndex = state.cart.cartIndex++;
			state.cart.items.push(orderItem);
		}
	},

	REMOVE_FROM_ORDER(state: OrdersState, index: number) {
		const updatedItems = state.cart.items.filter((item: MenuOrderItem) => {
			return item.cartIndex !== index;
		});
		Vue.set(state.cart, 'items', updatedItems);
	},

	SET_COSTS(state: OrdersState, payload: any) {
		Vue.set(state, 'items', payload.orderItems);
		Vue.set(state, 'costs', payload.costs);
		Vue.set(state, 'queue', payload.queue);

		// Merge the calculated discounts onto the cart items
		payload.orderItems?.forEach((orderItem: MenuOrderItem) => setCartItemDiscount(state, orderItem.cartIndex, orderItem.discount));
	},

	/**
	 * Set next order queue time
	 *
	 * @param {OrdersState} state
	 * @param {QueueInfo} queueInfo
	 * @return {void}
	 */
	SET_QUEUE(state: OrdersState, queueInfo: QueueInfo): void {
		Vue.set(state, 'queue', queueInfo);
	},

	SET_ACTIVE_MODIFICATION_REQUEST(state: OrdersState, modificationRequest: OrderModificationRequest | null): void {
		Vue.set(state, 'activeModificationRequest', modificationRequest);
	},

	/**
	 * Set tax rate of restaurant
	 *
	 * @param {OrdersState} state
	 * @param {Restaurant} restaurant
	 * @return {void}
	 */
	SET_RESTAURANT_CHARGES(state: OrdersState, restaurant: Restaurant): void {
		if(restaurant.service_charge) {
			// Get the value without the symbol ($, %)
			Vue.set(state.costs.serviceCharge, 'type', restaurant.service_charge.slice(restaurant.service_charge.length - 1) === '$' ? 'dollar' : 'percentage');

			// Set the percentage value for display purposes
			if(state.costs.serviceCharge.type === 'percentage') {
				Vue.set(state.costs.serviceCharge, 'percentageValue', +restaurant.service_charge.slice(0, -1));
			}
			Vue.set(state.costs.serviceCharge, 'label', restaurant.order_options?.service_charge_label || '');
		}
		if(restaurant.delivery && restaurant.delivery.delivery_charge) {
			const deliveryCharge = restaurant.delivery.delivery_charge;
			// Get the value without the symbol ($, %)
			Vue.set(state.costs.deliveryCharge, 'type', deliveryCharge.slice(deliveryCharge.length - 1) === '$' ? 'dollar' : 'percentage');

			// Set the percentage value for display purposes
			if(state.costs.deliveryCharge.type === 'percentage') {
				Vue.set(state.costs.deliveryCharge, 'percentageValue', +deliveryCharge.slice(0, -1));
			}
		}
	},

	SET_MODIFIED_FEES(state: OrdersState, { type, updatedFee }: { type: FeeType, updatedFee: CostsServiceCharge | CostsDeliveryCharge }) {
		Vue.set(state.costs, type, updatedFee);
	},

	SET_TAKEOUT_INFORMATION(state: OrdersState, takeoutInfo: Pickup) {
		Vue.set(state.checkout, 'pickup', takeoutInfo);
	},

	SET_DELIVERY_INFORMATION(state: OrdersState, deliveryInfo: DeliveryInfo) {
		Vue.set(state.checkout, 'delivery', deliveryInfo);
	},

	SET_PAYMENT_INFORMATION(state: OrdersState, paymentInfo: PaymentInfo) {
		Vue.set(state, 'checkout', {
			...state.checkout,
			...paymentInfo
		});
	},

	SET_CONTACT_INFORMATION(state: OrdersState, contactInfo: Contact) {
		Vue.set(state.checkout, 'contact', contactInfo);
	},

	SET_EVENT_INFORMATION(state: OrdersState, eventInfo: EventInfo | null) {
		Vue.set(state.checkout, 'cateringEvent', eventInfo);
	},

	SET_QUOTE_INFORMATION(state: OrdersState, quoteInfo: QuoteInfo | null) {
		Vue.set(state.checkout, 'quoteInfo', quoteInfo);
	},

	SET_CHECKOUT_ANSWERED_QUESTIONS(state: OrdersState, answeredQuestions: AnsweredCustomQuestion[]) {
		Vue.set(state.checkout, 'questions', answeredQuestions);
	},

	SET_CHECKOUT_AUTO_ACCEPT(state: OrdersState, autoAccept: boolean) {
		Vue.set(state.checkout, 'autoAccept', autoAccept);
	},

	SET_NOTES(state: OrdersState, notes: string) {
		if (!state.checkout.pickup) {
			Vue.set(state.checkout, 'pickup', { notes });
		}
		Vue.set(state.checkout, 'pickup', { ...state.checkout.pickup, notes });
	},

	/**
	 * Set the membership program info
	 *
	 * @param {OrdersState} state
	 * @param {CheckoutMemberInfo} payload
	 * @return {void}
	 */
	SET_MEMBER_INFO(state: OrdersState, payload: CheckoutMemberInfo): void {
		Vue.set(state.checkout, 'memberInfo', payload);
		// Preserve an explicit selection (staff-picked promos); only default when missing
		if (!(payload.promosApplied instanceof Set)) {
			Vue.set(state.checkout.memberInfo, 'promosApplied', new Set<string>());
		}
		const { contact, memberInfo } = state.checkout;

		// Set the contact info from what we can with the ELB hit
		if(memberInfo && memberInfo.phone && contact) {
			if(!contact.full_name) {
				contact.full_name = memberInfo.name || '';
			}
			if(!contact.email) {
				contact.email = memberInfo.email ? memberInfo.email : '';
			}
		}
	},

	/**
	 * Cache the raw multi-provider membership lookup result so the checkout
	 * selector can rehydrate (e.g. when the guest details modal is reopened).
	 *
	 * @param {OrdersState} state
	 * @param {MembershipLookupResult | null} payload
	 * @return {void}
	 */
	SET_MEMBERSHIP_LOOKUP(state: OrdersState, payload: MembershipLookupResult | null): void {
		Vue.set(state, 'membershipLookup', payload);
	},

	/**
	 * Set the member prices on items/options that contains the same promo
	 * of the member's payload.
	 * TODO - ACTUALLY APPLY THE MEMBER PRICES, CURRENTLY THIS IS ONLY USED TO FILL UP THE promosApplied ARRAY
	 *
	 * @param {OrdersState} state
	 * @param {Menu[]} menus
	 * @return {void}
	 */
	SET_MEMBER_PRICES(state: OrdersState, menus: Menu[]): void {
		if(state.checkout.memberInfo?.promos?.length) {
			// Set cart items' member price
			if(state.cart?.items?.length) {
				state.cart.items.forEach((item: MenuOrderItem) => {
					// We need to know if the cart item has pricing options because if it does we don't want to manipulate the price directly from the item
					const hasPricingOptions = item.orderOptions?.some((orderOption: OrderOptionGroup) => orderOption.optionGroupType === 'pricing') || false;
					compareItem(item, state.checkout.memberInfo!, 'orderOptions', hasPricingOptions);
				});
			}

			// Set menu items' member price
			menus.forEach((menu: Menu) => {
				// Section items
				if(menu.sections?.length) {
					menu.sections.forEach((section: MenuSection) => {
						if(section.items?.length) {
							section.items.forEach((item: MenuItem) => {
								compareItem(item, state.checkout.memberInfo!);
							});
						}
					});
				}
				// Menu items
				else if (menu.items?.length) {
					menu.items.forEach((item: MenuItem) => {
						compareItem(item, state.checkout.memberInfo!);
					});
				}
			});
		}
		Vue.set(state, 'memberMenus', menus);
	},

	/**
	 * Set the applied discount (promo code or manual percentage) to the costs state
	 * Amount is set to 0 since the discount calculation is done when set costs is called
	 *
	 * @param {OrdersState} state
	 * @param {DiscountInfoCodes | ManualDiscount} discount
	 * @return {void}
	 */
	SET_DISCOUNT(state: OrdersState, discount: DiscountInfoCodes | ManualDiscount | null): void {
		// Promo code
		if(discount && 'name' in discount && discount.name) {
			const discountType = discount.value.slice(discount.value.length - 1) === '$' ? 'dollar' : 'percentage';
			Vue.set(state.costs, 'discount', {
				amount: 0,
				percentageValue: discountType === 'percentage' ? +discount.value.slice(0, -1) : 0,
				type: discountType,
				code: discount.name
			});
		}
		// Manual percentage
		else if (discount && 'manual' in discount && discount.manual) {
			Vue.set(state.costs, 'discount', {
				amount: 0,
				percentageValue: discount.value,
				type: 'percentage',
				manual: true
			});
		}
		// Discount removed
		else {
			Vue.set(state.costs, 'discount', {
				amount: 0,
				percentageValue: 0,
				type: 'percentage',
				code: ''
			});
		}
	},

	/**
	 * Set or clear the per-item discount on the matching cart item.
	 *
	 * @param {OrdersState} state
	 * @param {{ item: MenuOrderItem, discount: CostsDiscount | null }} payload
	 * @return {void}
	 */
	SET_ITEM_DISCOUNT(state: OrdersState, { item, discount }: { item: MenuOrderItem, discount: CostsDiscount | null }): void {
		setCartItemDiscount(state, item.cartIndex, discount);
	},

	/**
	 * Merge the cart item with another item that has become identical (same item, options and discount).
	 * Runs after a discount change succeeds, since removing or applying a discount can make the item match another cart item.
	 * This must run after set costs succeeds after applying a discount, since when set costs fails the discount is reverted.
	 * For example: if a discount failed to be removed from an item, then the item would revert back to the original discount.
	 * However since the item was already merged with the identical items, the discount would then incorrectly be applied to all the items
	 *
	 * @param {OrdersState} state
	 * @param {MenuOrderItem} item
	 * @return {void}
	 */
	MERGE_MATCHING_CART_ITEM(state: OrdersState, item: MenuOrderItem): void {
		const index = state.cart.items.findIndex((cartItem: MenuOrderItem) => cartItem.cartIndex === item.cartIndex);
		if (index === -1) {
			return;
		}
		const currentItem = state.cart.items[index];
		const matchingIndex = state.cart.items.findIndex((cartItem: MenuOrderItem) => {
			if (cartItem.cartIndex === currentItem.cartIndex || cartItem.id !== currentItem.id || cartItem.openItem !== currentItem.openItem) {
				return false;
			}
			const tempItemOrderOptions = formatOrderOptionsToCompare(currentItem.orderOptions);
			const itemToCompareOrderOptions = formatOrderOptionsToCompare(cartItem.orderOptions as OrderOptionGroup[]);
			return JSON.stringify(tempItemOrderOptions) === JSON.stringify(itemToCompareOrderOptions) && (currentItem.discount?.percentageValue ?? null) === (cartItem.discount?.percentageValue ?? null);
		});
		if (matchingIndex === -1) {
			return;
		}
		const matchingItem = state.cart.items[matchingIndex];
		const merged: MenuOrderItem = { ...currentItem };
		merged.quantity += matchingItem.quantity;
		merged.orderPrice = (currentItem.orderPrice ?? 0) + (matchingItem.orderPrice ?? 0);
		// setCosts will return the two items separately, so sum their discount amounts into the merged item
		if (currentItem.discount && matchingItem.discount) {
			merged.discount = {
				...currentItem.discount,
				amount: (currentItem.discount.amount ?? 0) + (matchingItem.discount.amount ?? 0)
			};
		}
		Vue.set(state.cart.items, index, merged);
		state.cart.items.splice(matchingIndex, 1);
	},


	RESET_ITEM_VIEWER_STATE(state: OrdersState) {
		Vue.set(state, 'orderItem', null);
		Vue.set(state, 'orderItemPrice', 0);
		Vue.set(state, 'selectedItem', null);
		Vue.set(state, 'editingItem', false);
	},


	SET_CART(state: OrdersState, cart: Cart) {
		Vue.set(state, 'cart', cart);
	},

	CLEAR_CART(state: OrdersState) {
		Vue.set(state, 'cart', {
			items: [],
			cartIndex: 0
		});
		Vue.set(state, 'costs', {
			subtotal: 0,
			subtotalDiscounted: 0,
			taxBrackets: {},
			taxes: 0,
			tip: {
				percentageValue: 0,
				amount: 0,
				type: 'dollar'
			},
			serviceCharge: {
				percentageValue: 0,
				amount: 0,
				type: 'dollar',
				label: ''
			},
			deliveryCharge: {
				percentageValue: 0,
				amount: 0,
				type: 'dollar'
			},
			voucher: {
				code: '',
				balance: 0,
				amount: 0
			},
			discount: {
				percentageValue: 0,
				amount: 0,
				type: 'percentage',
				code: ''
			},
			memberDiscount: {
				preTax: false,
				amount: 0
			},
			subsidyDiscount: {
				type: '',
				chosenAmountToBePaid: undefined,
				code: undefined,
				amount: 0,
				baseSubsidizedAmount: 0
			},
			total: 0,
			totalWithTip: 0
		});
	},

	SET_ITEM_SORT(state: OrdersState, sort: OrderItemSort): void {
		Vue.set(state, 'itemSort', sort);
	},

	SET_ITEM_DIETS(state: OrdersState, diets: string[]): void {
		Vue.set(state, 'itemDiets', diets);
	},

	RESET_ORDERS_STATE(state: OrdersState) {
		Vue.set(state, 'orderDetails', {
			type: '',
			tableArea: '',
			tableLocation: '',
			tableNum: '',
			takeoutType: '',
			checkNum: '',
			menuManagerEvent: undefined,
			menuManagerEventBEO: undefined,
			menuManagerEventPackage: undefined,
			menuManagerLaneReservation: undefined
		});
		Vue.set(state, 'selectedItem', null);
		Vue.set(state, 'selectedSectionSlug', '');
		Vue.set(state, 'selectedOrder', {});
		Vue.set(state, 'orderItem', null);
		Vue.set(state, 'editingOrderItem', false);
		Vue.set(state, 'orderValidationErrors', {});
		Vue.set(state, 'repeatOrderNotices', null);
		Vue.set(state, 'orderItemPrice', 0);
		Vue.set(state, 'cart', {
			items: [],
			cartIndex: 0
		});
		Vue.set(state, 'checkout', {
			pickup: null,
			delivery: {
				type: ''
			},
			contact: {
				full_name: '',
				email: '',
				phone_number: '',
				password: ''
			},
			loyaltyProgram: null,
			memberInfo: null,
			questions: null,
			invoice: false,
			costCenter: '',
			purchaseOrder: ''
		});
		Vue.set(state, 'membershipLookup', null);
		Vue.set(state, 'costs', {
			subtotal: 0,
			subtotalDiscounted: 0,
			taxBrackets: {},
			taxes: 0,
			tip: {
				percentageValue: 0,
				amount: 0,
				type: 'dollar'
			},
			serviceCharge: {
				percentageValue: 0,
				amount: 0,
				type: 'dollar',
				label: ''
			},
			deliveryCharge: {
				percentageValue: 0,
				amount: 0,
				type: 'dollar'
			},
			voucher: {
				code: '',
				balance: 0,
				amount: 0
			},
			discount: {
				percentageValue: 0,
				amount: 0,
				type: 'percentage',
				code: ''
			},
			memberDiscount: {
				preTax: false,
				amount: 0
			},
			subsidyDiscount: {
				type: '',
				chosenAmountToBePaid: undefined,
				code: undefined,
				amount: 0,
				baseSubsidizedAmount: 0
			},
			total: 0,
			totalWithTip: 0
		});
		Vue.set(state, 'chosenCreditCard', null);
		Vue.set(state, 'isQuoting', false);
	}
};
